/**
 * Hebrew-aware search for Shopify Craft / Dawn-family themes.
 *
 * Why this exists: Shopify's storefront search and the Search & Discovery app
 * tokenise Hebrew poorly — no morphological clitic handling (ו/ה/ב/כ/ל/מ/ש),
 * no final-letter folding (ך/כ ם/מ ן/נ ף/פ ץ/צ), no nikud stripping, weak typo
 * tolerance. This script owns relevance; the theme still owns rendering of the
 * /search results grid (we reorder it client-side to match our ranking).
 *
 * What it does:
 *   1. Builds a lazy client-side index from /collections/all?view=search-index
 *      (a JSON-emitting Liquid template included in this package).
 *   2. Attaches a dropdown autosuggest to every <search-form> input on the page
 *      — products, collections, and pages.
 *   3. On the /search results page, transparently redirects a human Hebrew
 *      query to ?q=id:111 OR id:222… so the native grid renders the engine's
 *      matches, then restores the human query in the input/title and reorders
 *      the grid to engine ranking.
 *
 * Safe by design: every entry point is wrapped in try/catch; any failure
 * degrades to the theme's normal search behaviour (no dropdown, native grid).
 *
 * Zero dependencies. Plain ES module. No importmap required.
 */

/* ==========================================================================
 *  HEBREW ENGINE
 * ========================================================================== */

const CACHE_KEY = 'hs-craft-v1';
const CACHE_TTL_MS = 30 * 60 * 1000;
const INDEX_URL = '/collections/all?view=search-index';

const NIKUD = /[֑-ׇ]/g;
const QUOTES = /['"`׳״‘’“”]/g;
const FINALS = { ך: 'כ', ם: 'מ', ן: 'נ', ף: 'פ', ץ: 'צ' };
const FINALS_RE = /[ךםןףץ]/g;
const NONWORD = /[^א-תװ-ײa-z0-9]+/g;
const CLITICS = new Set(['ו', 'ה', 'ב', 'כ', 'ל', 'מ', 'ש']);

const FIELD_WEIGHTS = { t: 12, m: 10, s: 9, b: 8, v: 7, y: 5, g: 4 };
const MAX_FIELD_TOKENS = 48;

function normalize(s) {
  if (!s) return '';
  return s
    .normalize('NFKD')
    .replace(NIKUD, '')
    .replace(QUOTES, '')
    .toLowerCase()
    .replace(FINALS_RE, (ch) => FINALS[ch]);
}

function tokenize(s) {
  const out = normalize(s).split(NONWORD);
  const res = [];
  for (const t of out) if (t) res.push(t);
  return res;
}

function variants(tok) {
  const out = [tok];
  let cur = tok;
  for (let i = 0; i < 2; i++) {
    if (cur.length >= 3 && CLITICS.has(cur[0])) {
      const stem = cur.slice(1);
      if (stem.length >= 2 && !out.includes(stem)) out.push(stem);
      cur = stem;
    } else break;
  }
  return out;
}

function editDistance(a, b, max) {
  if (a === b) return 0;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > max) return max + 1;
  let prev = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;
  for (let i = 1; i <= la; i++) {
    const cur = new Array(lb + 1);
    cur[0] = i;
    let best = i;
    const ai = a[i - 1];
    for (let j = 1; j <= lb; j++) {
      const cost = ai === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (cur[j] < best) best = cur[j];
    }
    if (best > max) return max + 1;
    prev = cur;
  }
  return prev[lb];
}

function fuzzyBudget(len) {
  if (len >= 6) return 2;
  if (len >= 4) return 1;
  return 0;
}

function scoreFieldToken(qVars, field, weight) {
  let best = 0;
  for (const q of qVars) {
    if (field.exact.has(q)) {
      best = Math.max(best, weight);
      continue;
    }
    const budget = fuzzyBudget(q.length);
    for (const d of field.list) {
      if (d === q) {
        best = Math.max(best, weight);
      } else if (q.length >= 2 && d.startsWith(q)) {
        best = Math.max(best, weight * (0.62 + 0.16 * (q.length / d.length)));
      } else if (d.length >= 2 && q.startsWith(d)) {
        best = Math.max(best, weight * 0.5);
      } else if (budget > 0 && Math.abs(d.length - q.length) <= budget) {
        const dist = editDistance(q, d, budget);
        if (dist <= budget) best = Math.max(best, weight * (0.42 - 0.08 * dist));
      }
      if (best >= weight) break;
    }
  }
  return best;
}

function buildDoc(p) {
  const fields = {};
  const sources = [
    ['t', p.t],
    ['v', p.v],
    ['y', p.y],
    ['g', p.g],
    ['s', p.s],
  ];
  for (const [key, val] of sources) {
    const exact = new Set();
    const list = [];
    let count = 0;
    for (const tok of tokenize(val || '')) {
      for (const variant of variants(tok)) {
        if (exact.has(variant)) continue;
        exact.add(variant);
        list.push(variant);
        if (++count >= MAX_FIELD_TOKENS) break;
      }
      if (count >= MAX_FIELD_TOKENS) break;
    }
    fields[key] = { exact, list };
  }
  return {
    id: p.i,
    title: p.t,
    url: p.u,
    img: p.img,
    price: p.p,
    available: p.a !== 0,
    fields,
  };
}

async function pool(items, size, fn) {
  let idx = 0;
  const workers = new Array(Math.min(size, items.length)).fill(0).map(async () => {
    while (idx < items.length) {
      const cur = idx++;
      await fn(items[cur]);
    }
  });
  await Promise.all(workers);
}

async function fetchIndexPage(page) {
  const res = await fetch(`${INDEX_URL}&page=${page}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`search-index HTTP ${res.status}`);
  const text = await res.text();
  return JSON.parse(text);
}

class HebrewSearch {
  #ready = null;
  #docs = [];
  #collections = [];
  #pages = [];

  get loaded() {
    return this.#docs.length > 0;
  }

  load() {
    if (this.#ready) return this.#ready;
    this.#ready = this.#load().catch((err) => {
      this.#ready = null;
      throw err;
    });
    return this.#ready;
  }

  ready() {
    return this.load();
  }

  async #load() {
    const cached = this.#readCache();
    if (cached) {
      this.#ingest(cached);
      return;
    }
    const first = await fetchIndexPage(1);
    let products = first.products || [];
    const totalPages = first.pages || 1;
    const rest = [];
    for (let p = 2; p <= totalPages; p++) rest.push(p);
    await pool(rest, 6, async (p) => {
      const data = await fetchIndexPage(p);
      products = products.concat(data.products || []);
    });
    const payload = {
      products,
      collections: first.collections || [],
      pages: first.static || [],
      ts: Date.now(),
    };
    this.#writeCache(payload);
    this.#ingest(payload);
  }

  #ingest(payload) {
    this.#docs = (payload.products || []).map(buildDoc);
    this.#collections = payload.collections || [];
    this.#pages = payload.pages || [];
  }

  #readCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || Date.now() - data.ts > CACHE_TTL_MS) return null;
      if (!Array.isArray(data.products) || data.products.length === 0) return null;
      return data;
    } catch {
      return null;
    }
  }

  #writeCache(payload) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
    } catch {
      /* storage full / disabled — engine still works in-memory */
    }
  }

  #matchLinks(links, qVars, limit) {
    const scored = [];
    const seenUrls = new Set();
    for (const link of links) {
      if (seenUrls.has(link.u)) continue;
      seenUrls.add(link.u);
      const exact = new Set();
      const list = [];
      const handle = (link.h || '').replace(/[-_]+/g, ' ');
      for (const tok of tokenize(`${link.t} ${handle} ${link.d || ''}`)) {
        for (const v of variants(tok)) {
          if (!exact.has(v)) {
            exact.add(v);
            list.push(v);
          }
        }
      }
      const field = { exact, list };
      let score = 0;
      let missed = false;
      for (const qv of qVars) {
        const s = scoreFieldToken(qv, field, 1);
        if (s === 0) missed = true;
        score += s;
      }
      if (score > 0 && !(missed && qVars.length > 1)) {
        scored.push({ link, score });
      }
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit).map((x) => x.link);
  }

  search(query, opts = {}) {
    const limitP = opts.products ?? 8;
    const limitC = opts.collections ?? 3;
    const limitPg = opts.pages ?? 3;
    const empty = { products: [], collections: [], pages: [] };

    const qTokens = tokenize(query);
    if (qTokens.length === 0) return empty;
    const qVars = qTokens.map(variants);
    const qNorm = normalize(query).replace(NONWORD, ' ').trim();

    const scored = [];
    for (const doc of this.#docs) {
      let total = 0;
      let unmatched = 0;
      for (const qv of qVars) {
        let bestForToken = 0;
        for (const key in doc.fields) {
          const s = scoreFieldToken(qv, doc.fields[key], FIELD_WEIGHTS[key]);
          if (s > bestForToken) bestForToken = s;
        }
        if (bestForToken === 0) unmatched++;
        total += bestForToken;
      }
      if (unmatched > 0 && qVars.length > 1) continue;
      if (total === 0) continue;
      if (qNorm.length >= 2 && normalize(doc.title).indexOf(qNorm) !== -1) {
        total += 8;
      }
      scored.push({ doc, score: total, tlen: doc.title.length });
    }

    scored.sort((a, b) => b.score - a.score || a.tlen - b.tlen);

    return {
      products: scored.slice(0, limitP).map((x) => x.doc),
      collections: this.#matchLinks(this.#collections, qVars, limitC),
      pages: this.#matchLinks(this.#pages, qVars, limitPg),
    };
  }
}

const hebrewSearch = new HebrewSearch();

/* ==========================================================================
 *  STYLING — injected once, scoped to .hs-dropdown
 * ========================================================================== */

const CSS = `
/* Default: floating dropdown anchored to the search-form (for inline search inputs). */
.hs-dropdown {
  position: absolute;
  inset-block-start: calc(100% + 0.25rem);
  inset-inline: 0;
  z-index: 1000;
  max-block-size: min(70vh, 32rem);
  overflow-y: auto;
  background: var(--color-background, #fff);
  color: var(--color-foreground, rgba(18,18,18,0.95));
  border: 1px solid rgba(var(--color-foreground-rgb, 18,18,18), 0.1);
  border-radius: 6px;
  box-shadow: 0 12px 32px rgba(var(--color-shadow-rgb, 0,0,0), 0.12);
  font-family: var(--font-body-family, inherit);
  font-size: 0.9375rem;
  line-height: 1.4;
  text-align: start;
}
/* When inside the search modal, the dropdown flows in the modal's content area
   instead of floating — so the modal's own scroll handles overflow on mobile. */
.hs-dropdown[hidden] { display: none; }
.hs-dropdown__section { padding: 0.5rem 0; border-block-end: 1px solid rgba(var(--color-foreground-rgb, 18,18,18), 0.08); }
.hs-dropdown__section:last-child { border-block-end: 0; }
.hs-dropdown__heading {
  margin: 0;
  padding: 0.25rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.6;
}
.hs-dropdown__list { list-style: none; margin: 0; padding: 0; }
.hs-dropdown__item { margin: 0; }
.hs-dropdown__item a {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  min-block-size: 3rem;
  color: inherit;
  text-decoration: none;
  transition: background-color 0.15s ease;
}
.hs-dropdown__item a:hover,
.hs-dropdown__item[aria-selected="true"] a {
  background: rgba(var(--color-foreground-rgb, 18,18,18), 0.06);
  outline: 0;
}
.hs-dropdown__media {
  flex: 0 0 auto;
  inline-size: 3rem;
  block-size: 3rem;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(var(--color-foreground-rgb, 18,18,18), 0.05);
}
.hs-dropdown__media img {
  inline-size: 100%;
  block-size: 100%;
  object-fit: cover;
  display: block;
}
.hs-dropdown__body { flex: 1 1 auto; min-inline-size: 0; }
.hs-dropdown__title {
  display: block;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hs-dropdown__meta {
  display: block;
  font-size: 0.8125rem;
  opacity: 0.7;
  margin-block-start: 0.125rem;
}
.hs-dropdown__sold-out { font-size: 0.75rem; opacity: 0.6; padding-inline-start: 0.5rem; }
.hs-dropdown__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  padding: 0.25rem 1rem 0.5rem;
}
.hs-dropdown__chip {
  display: inline-block;
  padding: 0.25rem 0.625rem;
  border: 1px solid rgba(var(--color-foreground-rgb, 18,18,18), 0.2);
  border-radius: 999px;
  font-size: 0.8125rem;
  color: inherit;
  text-decoration: none;
  background: var(--color-background, #fff);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.hs-dropdown__chip:hover,
.hs-dropdown__chip[aria-selected="true"] {
  background: rgba(var(--color-foreground-rgb, 18,18,18), 0.06);
  border-color: rgba(var(--color-foreground-rgb, 18,18,18), 0.4);
  outline: 0;
}
.hs-dropdown__pages { padding: 0.25rem 0; }
.hs-dropdown__pages a { font-size: 0.875rem; padding-block: 0.375rem; }
.hs-dropdown__empty {
  padding: 1rem;
  text-align: center;
  opacity: 0.7;
  font-size: 0.875rem;
}
.hs-dropdown__view-all {
  display: block;
  padding: 0.625rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(var(--color-foreground-rgb, 18,18,18), 0.04);
  color: inherit;
  text-decoration: none;
  border-block-start: 1px solid rgba(var(--color-foreground-rgb, 18,18,18), 0.08);
  transition: background-color 0.15s ease;
}
.hs-dropdown__view-all:hover { background: rgba(var(--color-foreground-rgb, 18,18,18), 0.08); }

/* search-form provides positioning context for the dropdown */
search-form { position: relative; }

@media screen and (max-width: 749px) {
  .hs-dropdown:not(.hs-dropdown--inline) { max-block-size: 60vh; }
  .hs-dropdown__item a { padding: 0.75rem 1rem; min-block-size: 3.25rem; }
  .hs-dropdown__media { inline-size: 3.25rem; block-size: 3.25rem; }
  .hs-dropdown__chip { padding: 0.375rem 0.75rem; font-size: 0.875rem; }
}
  /* LALI / Craft search modal fix */
.search-modal .hs-dropdown,
.search-modal__content .hs-dropdown,
.search-modal__content-bottom .hs-dropdown {
  position: absolute !important;
  top: calc(100% + 6px) !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  min-width: 100% !important;
  max-height: 420px !important;
  overflow-y: auto !important;
  background: #fff !important;
  color: #222 !important;
  border: 1px solid rgba(0,0,0,0.12) !important;
  border-radius: 0 0 6px 6px !important;
  box-shadow: 0 12px 30px rgba(0,0,0,0.18) !important;
  z-index: 99999 !important;
  direction: rtl !important;
}

.search-modal search-form {
  position: relative !important;
  width: 100% !important;
  overflow: visible !important;
}

.search-modal .hs-dropdown__section,
.search-modal .hs-dropdown__list,
.search-modal .hs-dropdown__item {
  width: 100% !important;
}

.search-modal .hs-dropdown__item a {
  display: flex !important;
  flex-direction: row !important;
  width: 100% !important;
  box-sizing: border-box !important;
  justify-content: flex-start !important;
  align-items: center !important;
  text-align: right !important;
}

.search-modal .hs-dropdown__body {
  flex: 1 1 auto !important;
  min-width: 0 !important;
}

.search-modal .hs-dropdown__title,
.search-modal .hs-dropdown__meta {
  text-align: right !important;
  font-size: 14px !important;
}

.search-modal .hs-dropdown__media {
  width: 48px !important;
  height: 48px !important;
}
  /* LALI / Craft search modal typography fix */
.search-modal .hs-dropdown__heading {
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  padding: 10px 16px 6px !important;
  opacity: 0.65 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  text-align: right !important;
}

.search-modal .hs-dropdown__chips {
  padding: 10px 16px 14px !important;
  gap: 8px !important;
}

.search-modal .hs-dropdown__chip {
  font-size: 13px !important;
  line-height: 1.4 !important;
  padding: 6px 12px !important;
}

.search-modal .hs-dropdown__view-all {
  font-size: 14px !important;
  line-height: 1.4 !important;
  padding: 14px 16px !important;
  text-align: center !important;
  font-weight: 500 !important;
}

.search-modal .hs-dropdown__empty {
  font-size: 14px !important;
  padding: 16px !important;
}
  /* Hide native Shopify/Craft predictive search popup */
.search-modal predictive-search,
.search-modal predictive-search-results,
.search-modal #predictive-search-results,
.search-modal .predictive-search,
.search-modal .predictive-search-results {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}
`;

function injectStyles() {
  if (document.getElementById('hs-dropdown-styles')) return;
  const style = document.createElement('style');
  style.id = 'hs-dropdown-styles';
  style.textContent = CSS;
  document.head.appendChild(style);
}

/* ==========================================================================
 *  DROPDOWN ATTACH — one instance per <search-form>
 * ========================================================================== */

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[ch]);
}

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

class HebrewDropdown {
  constructor(form, input) {
    this.form = form;
    this.input = input;
    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('autocorrect', 'off');
    this.input.setAttribute('autocapitalize', 'off');
    this.input.setAttribute('spellcheck', 'false');

    this.form.setAttribute('autocomplete', 'off');
    this.el = document.createElement('div');
    this.el.className = 'hs-dropdown';
    this.el.setAttribute('role', 'listbox');
    this.el.hidden = true;

    this.form.appendChild(this.el);
    this.currentQuery = '';
    this.selectedIndex = -1;
    this.options = []; // [{el, href}]
    this.warmed = false;

    this.onInput = debounce(this.onInput.bind(this), 120);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', () => {
      this.warm();
      if (this.input.value.trim()) this.onInput();
    });
    this.input.addEventListener('keydown', (e) => this.onKey(e));
    document.addEventListener('click', (e) => {
      if (!this.form.contains(e.target)) this.hide();
    });
  }

  warm() {
    if (this.warmed) return;
    this.warmed = true;
    hebrewSearch.load().catch(() => {});
  }

  async onInput() {
    const q = this.input.value.trim();
    this.currentQuery = q;
    if (q.length < 1) {
      this.hide();
      return;
    }
    try {
      await hebrewSearch.ready();
    } catch {
      this.hide();
      return;
    }
    if (this.currentQuery !== q) return; // newer keystroke landed
    const { products, collections, pages } = hebrewSearch.search(q, {
      products: 8, collections: 4, pages: 4,
    });
    this.render(q, products, collections, pages);
  }

  render(q, products, collections, pages) {
    const hasAny = products.length || collections.length || pages.length;
    if (!hasAny) {
      this.el.innerHTML = `<div class="hs-dropdown__empty">לא נמצאו תוצאות עבור "${esc(q)}"</div>`;
      this.show();
      return;
    }

    let html = '';

    if (products.length) {
      html += '<div class="hs-dropdown__section">';
      html += '<h4 class="hs-dropdown__heading">מוצרים</h4>';
      html += '<ul class="hs-dropdown__list">';
      for (const p of products) {
        const media = p.img
          ? `<span class="hs-dropdown__media"><img src="${esc(p.img)}" alt="" width="48" height="48" loading="lazy"></span>`
          : `<span class="hs-dropdown__media"></span>`;
        const soldOut = p.available === false
          ? `<span class="hs-dropdown__sold-out">אזל מהמלאי</span>`
          : '';
        const price = p.price ? `<span class="hs-dropdown__meta">${esc(p.price)}${soldOut}</span>` : '';
        html += `<li class="hs-dropdown__item" role="option"><a href="${esc(p.url)}">${media}<span class="hs-dropdown__body"><span class="hs-dropdown__title">${esc(p.title)}</span>${price}</span></a></li>`;
      }
      html += '</ul></div>';
    }

    if (collections.length) {
      html += '<div class="hs-dropdown__section"><h4 class="hs-dropdown__heading">קטגוריות</h4><div class="hs-dropdown__chips">';
      for (const c of collections) {
        html += `<a class="hs-dropdown__chip" href="${esc(c.u)}" role="option">${esc(c.t)}</a>`;
      }
      html += '</div></div>';
    }

    if (pages.length) {
      html += '<div class="hs-dropdown__section hs-dropdown__pages"><h4 class="hs-dropdown__heading">דפים</h4><ul class="hs-dropdown__list">';
      for (const pg of pages) {
        html += `<li class="hs-dropdown__item" role="option"><a href="${esc(pg.u)}"><span class="hs-dropdown__body"><span class="hs-dropdown__title">${esc(pg.t)}</span></span></a></li>`;
      }
      html += '</ul></div>';
    }

    html += `<a class="hs-dropdown__view-all" href="/search?q=${encodeURIComponent(q)}&type=product">צפייה בכל התוצאות עבור "${esc(q)}"</a>`;

    this.el.innerHTML = html;
    this.options = Array.from(this.el.querySelectorAll('[role="option"], .hs-dropdown__view-all'));
    this.selectedIndex = -1;
    this.show();
  }

  show() {
    this.el.hidden = false;
  }

  hide() {
    this.el.hidden = true;
    this.selectedIndex = -1;
  }

  onKey(e) {
    if (this.el.hidden) {
      if (e.key === 'ArrowDown' && this.input.value.trim()) {
        this.onInput();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.move(-1);
    } else if (e.key === 'Enter') {
      if (this.selectedIndex >= 0) {
        e.preventDefault();
        const opt = this.options[this.selectedIndex];
        const href = opt.tagName === 'A' ? opt.getAttribute('href') : opt.querySelector('a')?.getAttribute('href');
        if (href) window.location.href = href;
      }
      // else: let the form submit normally to /search?q=...
    } else if (e.key === 'Escape') {
      this.hide();
    }
  }

  move(dir) {
    if (!this.options.length) return;
    const prev = this.selectedIndex;
    this.selectedIndex = (this.selectedIndex + dir + this.options.length) % this.options.length;
    if (prev >= 0) this.options[prev].removeAttribute('aria-selected');
    const cur = this.options[this.selectedIndex];
    cur.setAttribute('aria-selected', 'true');
    cur.scrollIntoView({ block: 'nearest' });
  }
}

function attachDropdowns() {
  const inputs = document.querySelectorAll('search-form input[type="search"], search-form input[name="q"]');
  for (const input of inputs) {
    const form = input.closest('search-form');
    if (!form || form.dataset.hsAttached) continue;
    form.dataset.hsAttached = '1';
    try {
      new HebrewDropdown(form, input);
    } catch (err) {
      /* never let a single broken instance kill the page */
      console.warn('[hebrew-search] dropdown attach failed:', err);
    }
  }
}

/* ==========================================================================
 *  RESULTS PAGE — redirect human query to id-list, restore + reorder
 * ========================================================================== */

const STASH_KEY = 'hs-spq';
const PAGE_LIMIT = 60;

function currentQuery() {
  return (new URLSearchParams(location.search).get('q') || '').trim();
}

function restoreQueryInput(humanQuery) {
  const inputs = document.querySelectorAll('input[name="q"]');
  for (const input of inputs) {
    if (input instanceof HTMLInputElement && input.value.trim().startsWith('id:')) {
      input.value = humanQuery;
    }
  }
}

function reorderGrid(ids) {
  // Try multiple selectors — Craft/Dawn variants differ slightly
  const selectors = [
    '#product-grid > li',
    '.product-grid > li',
    '.grid--peek > li',
    '.collection .grid > li',
    'ul.grid li.grid__item',
  ];
  let items = [];
  let parent = null;
  for (const sel of selectors) {
    const found = document.querySelectorAll(sel);
    if (found.length >= 2) {
      items = Array.from(found);
      parent = items[0].parentElement;
      break;
    }
  }
  if (!parent || !items.length) return;

  const rank = new Map();
  let foundAny = false;
  ids.forEach((id, i) => rank.set(String(id), i));

  for (const el of items) {
    // Try to find a product id inside each grid item
    const pidEl = el.querySelector('[data-product-id]');
    const id = pidEl?.getAttribute('data-product-id')
      || el.getAttribute('data-product-id')
      || el.querySelector('a[href*="/products/"]')?.getAttribute('href')?.match(/\/products\/([^?#]+)/)?.[1];
    if (id && rank.has(String(id))) {
      foundAny = true;
      el.dataset.hsRank = String(rank.get(String(id)));
    } else {
      el.dataset.hsRank = '999999';
    }
  }

  if (!foundAny) return;

  const ordered = items
    .filter((el) => el.parentElement === parent)
    .sort((a, b) => Number(a.dataset.hsRank) - Number(b.dataset.hsRank));

  const fragment = document.createDocumentFragment();
  for (const el of ordered) fragment.appendChild(el);
  parent.appendChild(fragment);
}

function onTransformedPage() {
  let stash;
  try { stash = JSON.parse(sessionStorage.getItem(STASH_KEY) || 'null'); } catch { stash = null; }
  if (!stash || !Array.isArray(stash.ids)) {
    restoreQueryInput('');
    return;
  }
  restoreQueryInput(stash.q || '');

  if (stash.q) {
    const idList = /id:\d+(?:\s+OR\s+id:\d+)*/g;
    if (idList.test(document.title)) {
      document.title = document.title.replace(idList, stash.q);
    }
    // Generic: scrub id:N references out of any visible "no results" copy
    document.querySelectorAll('.search__no-results, .search-results-empty, h1, h2').forEach((el) => {
      if (el.textContent && /id:\d+/.test(el.textContent)) {
        el.textContent = el.textContent.replace(/id:\d+(?:\s+OR\s+id:\d+)*/g, stash.q);
      }
    });
  }

  reorderGrid(stash.ids);
}

async function transformHumanQuery(humanQuery) {
  let ids = [];
  try {
    await hebrewSearch.ready();
    ids = hebrewSearch.search(humanQuery, { products: PAGE_LIMIT }).products.map((d) => d.id);
  } catch {
    return; // engine unavailable — keep native results
  }

  try {
    sessionStorage.setItem(STASH_KEY, JSON.stringify({ q: humanQuery, ids, ts: Date.now() }));
  } catch { /* storage disabled */ }

  const url = new URL(location.href);
  url.searchParams.set('q', ids.length ? ids.map((id) => `id:${id}`).join(' OR ') : 'id:0');
  url.searchParams.set('type', 'product');
  location.replace(url.toString());
}

function initResultsPage() {
  const q = currentQuery();
  if (!q) return;
  if (q.startsWith('id:')) {
    onTransformedPage();
  } else {
    transformHumanQuery(q);
  }
}

/* ==========================================================================
 *  BOOTSTRAP
 * ========================================================================== */

function init() {
  try {
    injectStyles();
    attachDropdowns();
    // Re-attach if the theme inserts new search-form elements later (e.g. drawer open)
    const mo = new MutationObserver(() => attachDropdowns());
    mo.observe(document.body, { childList: true, subtree: true });

    if (location.pathname === '/search' || location.pathname.endsWith('/search')) {
      initResultsPage();
    }
  } catch (err) {
    console.warn('[hebrew-search] init failed:', err);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
